import { Directive, ElementRef, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { FormControlName, NgModel } from '@angular/forms';

import { first } from 'rxjs/operators';

const placeholders = {
  'X': '^[a-zA-ZA-zА-яЁё]',
  '9': '\\d',
  '0': '^[a-zA-ZA-zА-яЁё0-9]'
};

const keys = {
  'BACKSPACE': 8,
  'LEFT': 37,
  'RIGHT': 39,
  'DEL': 46,
  'ENTER': 13
};
interface IState {
  value: string;
}
@Directive({
  selector: '[maskedInput], [maskedInput][ngModel], [maskedInput][formControlName], [maskedInput][formControl]'
})
export class AxisInputMaskDirective implements OnInit {
  private state: IState;
  private increaseCursorValue = false;
  private blur = false; // used to set invalid state only after blur.

  @Input('maskedInput') format: any;
  @Input() separator = '?';
  @Input() preserveValue = false; // if true, mask input field will not be cleared and mark as invalid.

  @Output() rawChange = new EventEmitter();

  /**
   *
   * @param element
   * @param model
   * @param formControlName
   */
  constructor(private element: ElementRef,
    private model: NgModel,
    private formControlName: FormControlName) {
    this.state = {
      value: this.getValue()
    };
  }

  ngOnInit() {
    if (this.formControlName && this.formControlName.valueChanges) {
      this.formControlName.valueChanges
        .pipe(
          first()
        )
        .subscribe(value => {
          this.checkValidation();
        });
    } else if (
      this.model &&
      this.model.valueChanges &&
      this.model.hasOwnProperty('viewModel')
    ) {
      this.model.valueChanges.pipe(first()).subscribe(value => {
        this.checkValidation();
      });
    }
    if (this.model.hasOwnProperty('viewModel')) {
      setTimeout(() => {
        this.model.update.emit(this.getValue());
      }, 0);
    } else if (this.formControlName && this.formControlName.control) {
      this.formControlName.control.setValue(this.getValue());
    }
    this.applyMask(this.getClearValue(this.getValue()));
  }

  private checkValidation() {
    if (this.format.split(this.separator)[0].length > this.getValue().length) {
      if (!this.preserveValue) {
        this.setValue('');
      } else {
        this.setError();
      }
    }
  }

  /**
   *
   */
  @HostListener('input')
  public onChange(): void {
    this.applyMask(this.getClearValue(this.getValue()));
  }

  /**
   *
   * @param event
   */
  @HostListener('keypress', ['$event'])
  public onKeyPress(event): void {
    if (!this.format) {
      return;
    }
    const key = this.getKey(event);
    if (key === keys.BACKSPACE || key === keys.LEFT || key === keys.RIGHT) {
      return;
    }
    const cursorPosition = this.getCursorPosition();
    const regexp = this.createRegExp(cursorPosition);

    if (regexp != null && !regexp.test(event.key) || this.getValue().length >= this.format.length) {
      if (key === keys.ENTER) {
        return;
      }
    }
  }

  /**
   *
   * @param event
   */
  @HostListener('keydown', ['$event'])
  public onKeyDown(event): void {
    const key = this.getKey(event);
    const regex = '^[-\\/\\\\^$#&@№:<>_\\^!*+' + this.separator + '.()|\\[\\]{}]';
    const regexExpression = new RegExp(regex);
    this.increaseCursorValue = !regexExpression.test(event.key);
    if (key === keys.BACKSPACE || key === keys.DEL) { // in case of delete no need to move cursor position
      this.increaseCursorValue = false;
    }
    if ((key === keys.BACKSPACE || key === keys.DEL) && this.getClearValue(this.getValue()).length === 1) {
      if (this.preserveValue) {
        this.checkValidation();
      } else {
        this.setValue('');
        this.state.value = '';
        this.rawChange.emit('');
      }
    }
  }

  /**
   *
   * @param event
   */
  @HostListener('blur', ['$event'])
  public onBlur(event): void {
    this.blur = true;

    if (!this.format) {
      return;
    }
    const key = this.getKey(event);
    if (key === keys.BACKSPACE || key === keys.LEFT || key === keys.RIGHT) {
      return;
    }
    if (this.format.split(this.separator)[0].length > this.getValue().length) {
      if (this.preserveValue) {
        this.checkValidation();
      } else {
        this.setValue('');
      }
    }
  }

  /**
   *
   * @param event
   */
  private getKey(event) {
    return event.keyCode || event.charCode;
  }

  /**
   *
   * @param value
   */
  private applyMask(value): void {
    if (!this.format || !value) {
      return;
    }
    let newValue = '';
    let maskPosition = 0;
    if (this.getClearValue(value).length > this.getClearValue(this.format).length) {
      this.setValue(this.state.value);
      return;
    }

    for (let i = 0; i < value.length; i++) {
      const current = value[i];
      const regexp = this.createRegExp(maskPosition);
      if (regexp != null) {
        if (!regexp.test(current)) {
          this.setValue(this.state.value);
          break;
        }
        newValue += current;
      } else if (this.format[maskPosition] === current) {
        newValue += current;
      } else {
        newValue += this.format[maskPosition];
        i--;
      }
      maskPosition++;
    }
    // const nextMaskElement = this.format[maskPosition];
    // if (value.length && nextMaskElement && /^[-\/\\^$#&@№:<>_\^!*+?.()|\[\]{}]/.test(nextMaskElement)) {
    //   newValue += nextMaskElement;
    // }

    const oldValue = this.state.value;
    let cursorPosition = this.getCursorPosition();
    const currentCursorPositionValue = this.format[cursorPosition - 1];
    const regex = '^[-\\/\\\\^$#&@№:<>_\\^!*+' + this.separator + '.()|\\[\\]{}]';
    const regexExpression = new RegExp(regex);
    if (this.increaseCursorValue && regexExpression.test(currentCursorPositionValue)) {
      cursorPosition++;
    }
    this.increaseCursorValue = false;
    this.setValue(newValue);
    this.state.value = newValue;
    if (!oldValue || oldValue.length >= cursorPosition) {
      this.setCursorPosition(cursorPosition);
    }
    if (this.preserveValue) {
      this.checkValidation();
    }
  }

  /**
   *
   * @param position
   */
  private createRegExp(position): RegExp | null {
    if (!this.format) {
      return;
    }
    this.format = this.format.toUpperCase();
    if (!this.format[position]) {
      return null;
    }
    const currentSymbol = this.format[position].toUpperCase();
    const objectKeys = Object.keys(placeholders);
    const searchPosition = objectKeys.indexOf(currentSymbol);
    if (searchPosition >= 0) {
      return new RegExp(placeholders[objectKeys[searchPosition]], 'gi');
    }
    return null;
  }

  private getValue(): string {
    return this.element.nativeElement.value;
  }

  /**
   *
   * @param value
   */
  private getClearValue(value): string {
    return value.trim().replace(/[-\/\\^$#&@№:<>_\^!*+?.() |\[\]{}]/gi, '').replace(this.separator, '');
  }

  /**
   *
   * @param value
   */
  private setValue(value: string): void {
    this.element.nativeElement.value = value;
    if (this.formControlName && this.formControlName.control) {
      this.formControlName.control.setValue(value);
    } else if (this.model.hasOwnProperty('viewModel')) {
      setTimeout(() => {
        this.model.update.emit(value);
      }, 0);
    }
  }

  /**
   * set error when field touched, has value and blurred.
   */
  private setError() {
    if (this.formControlName.control.touched && this.formControlName.control.value && this.blur) {
      this.formControlName.control.setErrors({ maskInput: { valid: false } });
    }
  }

  private getCursorPosition(): number {
    return this.element.nativeElement.selectionStart;
  }

  /**
   *
   * @param start
   * @param end
   */
  private setCursorPosition(start: number, end: number = start): void {
    this.element.nativeElement.setSelectionRange(start, end);
  }
}
